import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ProcessVisualization = ({ processes }) => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
      <div className="text-center mb-6 md:mb-8 lg:mb-12">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-3 md:mb-4"
        >
          Our Service Delivery Process
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto"
        >
          A systematic approach ensuring quality delivery and client satisfaction at every stage
        </motion.p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
        <div className="space-y-3 md:space-y-4">
          {processes?.map((process, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setActiveStep(index)}
              className={`relative p-4 md:p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                activeStep === index
                  ? 'bg-primary text-primary-foreground shadow-lg scale-105'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activeStep === index ? 'bg-white/20' : 'bg-primary/10'
                  }`}
                >
                  <span
                    className={`text-base md:text-lg font-bold ${
                      activeStep === index ? 'text-white' : 'text-primary'
                    }`}
                  >
                    {index + 1}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className={`text-base md:text-lg font-semibold mb-1 md:mb-2 ${
                      activeStep === index ? 'text-white' : 'text-foreground'
                    }`}
                  >
                    {process.title}
                  </h3>
                  <p
                    className={`text-xs md:text-sm ${
                      activeStep === index ? 'text-white/90' : 'text-muted-foreground'
                    }`}
                  >
                    {process.description}
                  </p>
                  <div className="flex items-center gap-2 mt-2 md:mt-3">
                    <Icon
                      name="Clock"
                      size={14}
                      color={activeStep === index ? 'white' : 'var(--color-muted-foreground)'}
                    />
                    <span
                      className={`text-xs ${
                        activeStep === index ? 'text-white/80' : 'text-muted-foreground'
                      }`}
                    >
                      {process.duration}
                    </span>
                  </div>
                </div>
              </div>

              {activeStep === index && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 rounded-b-xl"
                />
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          key={activeStep}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="bg-muted rounded-xl p-6 md:p-8 lg:p-10"
        >
          <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary flex items-center justify-center">
              <Icon name={processes?.[activeStep]?.icon} size={24} color="white" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
                {processes?.[activeStep]?.title}
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground">
                Step {activeStep + 1} of {processes?.length}
              </p>
            </div>
          </div>

          <div className="space-y-3 md:space-y-4">
            <p className="text-sm md:text-base text-muted-foreground">
              {processes?.[activeStep]?.detailedDescription}
            </p>

            <div className="space-y-2 md:space-y-3">
              <p className="text-xs md:text-sm font-medium text-foreground">Key Activities:</p>
              {processes?.[activeStep]?.activities?.map((activity, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-start gap-2"
                >
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <span className="text-xs md:text-sm text-muted-foreground">{activity}</span>
                </motion.div>
              ))}
            </div>

            <div className="pt-4 md:pt-6 border-t border-border">
              <div className="flex items-center justify-between text-xs md:text-sm">
                <span className="text-muted-foreground">Estimated Duration:</span>
                <span className="font-medium text-foreground">{processes?.[activeStep]?.duration}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProcessVisualization;